<template>
    <generic-page>
        <div class="mb-2">
            <b-btn
                variant="link"
                size="lg"
                class="pl-0"
                to="/affiliate/reports"
            >
                <font-awesome-icon
                    size="lg"
                    icon="fa-light fa-chevron-left"
                />
                Reports
            </b-btn>
        </div>

        <div class="mb-3 mt-2">
            <h5 class="mb-0">{{ $t("Landing pages report") }}</h5>
        </div>

        <div v-if="loading">
            <generic-loading />
        </div>
        <div v-else-if="!rows.length" class="text-muted small py-3">
            {{ $t("No landing-page activity yet.") }}
        </div>
        <div v-else>
            <b-table-simple striped small responsive class="mb-0">
                <thead>
                    <tr>
                        <th>{{ $t("Landing") }}</th>
                        <th class="text-right">{{ $t("Earnings") }}</th>
                        <th class="text-right">{{ $t("Signups") }}</th>
                        <th class="text-right">{{ $t("Buyers") }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="row in rows" :key="row.landing">
                        <td class="align-middle">{{ row.landing }}</td>
                        <td class="align-middle text-right">
                            {{ formatUsd(row.earningsUsd) }}
                        </td>
                        <td class="align-middle text-right">
                            {{ row.signups }}
                        </td>
                        <td class="align-middle text-right">
                            {{ row.newBuyers }}
                        </td>
                    </tr>
                </tbody>
            </b-table-simple>

            <div class="d-flex justify-content-end mt-2">
                <b-btn
                    v-if="copied"
                    variant="success"
                    size="sm"
                    disabled
                >
                    <font-awesome-icon icon="fa-light fa-check" />
                    {{ $t("Copied — paste into Excel") }}
                </b-btn>
                <b-btn
                    v-else
                    variant="outline-primary"
                    size="sm"
                    :disabled="!rows.length"
                    @click="copyTable"
                >
                    <font-awesome-icon icon="fa-light fa-copy" />
                    {{ $t("Copy for Excel") }}
                </b-btn>
            </div>
        </div>

        <h6 class="text-muted text-uppercase mt-5 mb-3" style="font-size: 0.75rem; letter-spacing: 0.05em;">
            {{ $t("How attribution works") }}
        </h6>

        <div class="bg-white rounded p-3 mb-2">
            <div class="d-flex">
                <div class="mr-3">
                    <font-awesome-icon
                        class="branded"
                        icon="fa-duotone fa-shield-check"
                        size="lg"
                    />
                </div>
                <div>
                    <div class="font-weight-bold mb-1">
                        {{ $t("Attribution is permanent") }}
                    </div>
                    <div class="small">
                        Once a visitor clicks your link in their browser,
                        you're their affiliate — for every signup, purchase,
                        and commission they ever generate. Returning through
                        an email link, a search result, or by typing the URL
                        directly doesn't reassign them.
                    </div>
                </div>
            </div>
        </div>

        <div class="bg-white rounded p-3 mb-2">
            <div class="d-flex">
                <div class="mr-3">
                    <font-awesome-icon
                        class="branded"
                        icon="fa-duotone fa-route"
                        size="lg"
                    />
                </div>
                <div>
                    <div class="font-weight-bold mb-1">
                        {{ $t("The landing column shows the moment, not the source") }}
                    </div>
                    <div class="small">
                        People don't always sign up on the first visit. They
                        come back through email links, click around, read the
                        Terms, and eventually convert. The Landing column
                        reflects where each person was when their visit was
                        logged — sometimes the original page they arrived
                        through, often somewhere else along the way.
                    </div>
                </div>
            </div>
        </div>
    </generic-page>
</template>

<script>
import copy from "clipboard-copy";

export default {
    data() {
        return {
            loading: false,
            rows: [],
            copied: false,
            copiedTimer: null,
        };
    },
    created() {
        this.loadReport();
    },
    beforeDestroy() {
        if (this.copiedTimer) {
            clearTimeout(this.copiedTimer);
        }
    },
    methods: {
        loadReport() {
            this.loading = true;

            this.api
                .post("/affiliate/reports/landing")
                .then((response) => {
                    if (response?.data?.rows) {
                        this.rows = response.data.rows;
                    }
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        formatUsd(value) {
            return `$${Math.round(Number(value) || 0).toLocaleString()}`;
        },
        copyTable() {
            // TSV — Excel auto-splits across cells on paste, raw numbers
            // (no $ / no commas) land as numeric values for sum/sort/filter.
            const headers = ["Landing", "Earnings (USD)", "Signups", "Buyers"];
            const rows = this.rows.map((row) => [
                row.landing,
                Math.round(Number(row.earningsUsd) || 0),
                row.signups,
                row.newBuyers,
            ]);
            const tsv = [headers, ...rows]
                .map((cols) => cols.join("\t"))
                .join("\n");

            copy(tsv);

            this.copied = true;
            if (this.copiedTimer) {
                clearTimeout(this.copiedTimer);
            }
            this.copiedTimer = setTimeout(() => {
                this.copied = false;
                this.copiedTimer = null;
            }, 3000);
        },
    },
};
</script>
